import React, { useState } from "react";
import axios from "axios";

export default function LicenseExpired({ licenseStatus, onLicenseActivated }) {
  const [licenseKey, setLicenseKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleActivate = async (e) => {
    e.preventDefault();
    
    if (!licenseKey.trim()) {
      setError("Please enter a license key");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await axios.post("/api/License/activate", {
        licenseKey: licenseKey.trim().toUpperCase()
      });

      setSuccess(`License activated successfully! Valid until ${new Date(response.data.expiryDate).toLocaleDateString()}`);
      
      // Notify parent component
      if (onLicenseActivated) {
        setTimeout(() => {
          onLicenseActivated();
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to activate license");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.iconContainer}>
          <span style={styles.icon}>🔐</span>
        </div>
        
        <h1 style={styles.title}>License Required</h1>
        
        {licenseStatus?.isExpired ? (
          <div style={styles.expiredBanner}>
            <span style={styles.warningIcon}>⚠️</span>
            <div>
              <strong>License Expired</strong>
              <p style={styles.expiredText}>
                Your license expired on {new Date(licenseStatus.expiryDate).toLocaleDateString()}
              </p>
            </div>
          </div>
        ) : (
          <p style={styles.subtitle}>
            {licenseStatus?.message || "Please enter a valid license key to access the application."}
          </p>
        )}

        {licenseStatus?.licensedTo && (
          <div style={styles.licenseInfo}>
            <p><strong>Licensed To:</strong> {licenseStatus.licensedTo}</p>
            <p><strong>License Type:</strong> {licenseStatus.licenseType}</p>
          </div>
        )}

        <form onSubmit={handleActivate} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>License Key</label>
            <input
              type="text"
              value={licenseKey}
              onChange={(e) => setLicenseKey(e.target.value.toUpperCase())}
              placeholder="XXXX-XXXX-XXXX-XXXX"
              style={styles.input}
              maxLength={19}
            />
          </div>

          {error && <div style={styles.error}>{error}</div>}
          {success && <div style={styles.success}>{success}</div>}

          <button
            type="submit"
            disabled={loading}
            style={styles.button}
          >
            {loading ? "Activating..." : "🔓 Activate License"}
          </button>
        </form>

        <div style={styles.footer}>
          <p style={styles.footerText}>
            Don't have a license key? Contact your administrator or purchase a license.
          </p>
          <p style={styles.supportText}>
            📧 Support: support@uptechbanking.com
          </p>
        </div>
      </div>

      <div style={styles.brandFooter}>
        <p>Uptech Banking System v1.0</p>
        <p>© {new Date().getFullYear()} Uptech Solutions. All rights reserved.</p>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f0f2f5",
    padding: "20px"
  },
  card: {
    backgroundColor: "white",
    borderRadius: "16px",
    padding: "40px",
    maxWidth: "450px",
    width: "100%",
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
    textAlign: "center"
  },
  iconContainer: {
    marginBottom: "20px"
  },
  icon: {
    fontSize: "64px"
  },
  title: {
    fontSize: "28px",
    fontWeight: "bold",
    color: "#333",
    marginBottom: "10px"
  },
  subtitle: {
    color: "#666",
    marginBottom: "30px",
    fontSize: "14px"
  },
  expiredBanner: {
    display: "flex",
    alignItems: "center",
    gap: "15px",
    backgroundColor: "#fff3cd",
    border: "1px solid #ffc107",
    borderRadius: "8px",
    padding: "15px",
    marginBottom: "25px",
    textAlign: "left"
  },
  warningIcon: {
    fontSize: "32px"
  },
  expiredText: {
    margin: "5px 0 0 0",
    fontSize: "13px",
    color: "#856404"
  },
  licenseInfo: {
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    padding: "15px",
    marginBottom: "25px",
    textAlign: "left",
    fontSize: "14px"
  },
  form: {
    textAlign: "left"
  },
  inputGroup: {
    marginBottom: "20px"
  },
  label: {
    display: "block",
    marginBottom: "8px",
    fontWeight: "600",
    color: "#333",
    fontSize: "14px"
  },
  input: {
    width: "100%",
    padding: "14px",
    fontSize: "18px",
    fontFamily: "monospace",
    letterSpacing: "2px",
    border: "2px solid #ddd",
    borderRadius: "8px",
    boxSizing: "border-box",
    textAlign: "center",
    textTransform: "uppercase"
  },
  button: {
    width: "100%",
    padding: "14px",
    fontSize: "16px",
    fontWeight: "600",
    backgroundColor: "#1a73e8",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    marginTop: "10px"
  },
  error: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
    padding: "12px",
    borderRadius: "8px",
    marginBottom: "15px",
    fontSize: "14px"
  },
  success: {
    backgroundColor: "#d4edda",
    color: "#155724",
    padding: "12px",
    borderRadius: "8px",
    marginBottom: "15px",
    fontSize: "14px"
  },
  footer: {
    marginTop: "30px",
    paddingTop: "20px",
    borderTop: "1px solid #eee"
  },
  footerText: {
    color: "#888",
    fontSize: "13px",
    marginBottom: "10px"
  },
  supportText: {
    color: "#1a73e8",
    fontSize: "13px"
  },
  brandFooter: {
    marginTop: "30px",
    textAlign: "center",
    color: "#999",
    fontSize: "12px"
  }
};

